import React, {useState} from "react";
import LanguageSelector from "./LanguageSelector";
import SectionHeader from "./SectionHeader"
import TemplateSection from "./TemplateSection";
import ObjectID from "bson-objectid";
import _ from "lodash"
import {generateFieldId, generateSectionId} from "./TemplateEditorLogic"

import {
  createLocalPagesBrand,
  saveLocalPagesTemplate,
  updateLocalPagesBrand
} from "../manageLocations/localPages/LocalPagesApi";
import {DragDropContext, Droppable, Draggable} from "react-beautiful-dnd";




// a little function to help us with reordering the result
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const getItemStyle = (isDragging, draggableStyle) => ({
  // some basic styles to make the items look a bit nicer
  userSelect: "none",
  // change background colour if dragging
  background: "var(--card-bg)",
  borderRadius: isDragging ? "var(--card-border-radius)" : "0px",
  border: isDragging ? "2px solid var(--primary)" : "var(--border-width) solid var(--border-color) none none none",
  padding: isDragging ? "0 .75rem" : "0",
  // styles we need to apply on draggables
  ...draggableStyle
});

export default function TemplateEditor({
                                         localPagesTemplate,
                                         doneEditingFunction,
                                         brandsForTemplate,
                                         brandsForAccount,
                                         createMode,
                                         deleteFunction
                                       }) {

  /*
  Add a unique id to each component so they are easy to key for React
            ^^^^ unique id is never used
   */
  const initSections = () => {
    const copy = localPagesTemplate.sections.map(section => {
      section.uniqueId = ObjectID().toString()
      return section
    })
    return _.cloneDeep(copy)
  }

  const [templateName, setTemplateName] = useState(localPagesTemplate.templateName)
  const [themeId, setThemeId] = useState(localPagesTemplate.themeId)
  const [brands, setBrands] = useState(brandsForTemplate)
  const [sections, setSections] = useState(initSections())
  const [formIsDirty, setFormIsDirty] = useState(false);




  function isBlank(str) {
    return (!str || /^\s*$/.test(str));
  }

  const saveDisabled = () => {
    const brandsEmpty = brands.every(brand => {
      const answer = isBlank(brand.name) || isBlank(brand.vendorId)
      return answer
    })
    const answer = isBlank(themeId) || brandsEmpty || !formIsDirty
    return answer
  }

  const save = () => {
    saveBrands()
    //if section id or field id is blank generate one
    const newSections = localPagesTemplate.sections.map(section => {
      if (!section.sectionId) {
        section.sectionId = generateSectionId(section.sectionDisplayName, sections)
      }

      const newFields = section.fields.map(field => {
        if (!!field.fieldId) {
          return field
        } else {
          field.fieldId = generateFieldId(field.fieldDisplayName, sections)
          return field
        }
      })
      section.fields = newFields
      return section
    })
    saveLocalPagesTemplate(localPagesTemplate).then(resp => {
      doneEditingFunction()
    })
  }

  const saveBrands = () => {
    const id = "_id"
    const brandsAlreadyOnTemplate = brands.filter(brand => brand.hasOwnProperty(id))

    //Save any new brands added
    const addedBrands = brands.filter(brand => !brand.hasOwnProperty(id))
    //If it's already in mongo update it
    const addedButAlreadyInMongo = brandsForAccount.filter(brand => addedBrands.map(brand => brand.vendorId).includes(brand.vendorId))
    const notInMongo = addedBrands.filter(brand => !addedButAlreadyInMongo.map(brand => brand.vendorId).includes(brand.vendorId))

    brandsAlreadyOnTemplate.map(brand => {
      updateLocalPagesBrand(brand)
    })

    addedButAlreadyInMongo.map(brand => {
      const newName = brands.find(b => b.vendorId === brand.vendorId).name
      brand.name = newName
      updateLocalPagesBrand(brand)
    })

    notInMongo.map(brand => {
      createLocalPagesBrand(brand)
    })
  }

  const cancel = async () => {
    if (createMode) {
      await deleteFunction(localPagesTemplate)
    }
    doneEditingFunction()
  }

  const nameChanged = (e) => {
    localPagesTemplate.templateName = e.target.value;
    setTemplateName(localPagesTemplate.templateName)
    setFormIsDirty(true)
  }

  const themeChanged = (e) => {
    localPagesTemplate.themeId = e.target.value;
    setThemeId(e.target.value)
    setFormIsDirty(true)
  }

  const languageSelected = (language, code) => {
    localPagesTemplate.language = code
    setFormIsDirty(true)
  }

  const brandsUpdated = () => {
    const myBrands = [...brands]
    localPagesTemplate.brandIds = myBrands.map(brand => brand.vendorId)
    setBrands(myBrands)
    setFormIsDirty(true)
  }

  const brandNameChanged = (event, index) => {
    brands[index].name = event.target.value;
    brandsUpdated()
  }

  const brandIdChanged = (value, index) => {
    brands[index].vendorId = value;
    brandsUpdated()
  }

  const addBrand = () => {
    const x = {name: "My New Brand", vendor: "DevHub", vendorId: ""}
    brands.push(x)
    brandsUpdated()
  }

  const removeBrand = (index) => {
    brands.splice(index, 1)
    brandsUpdated()
  }
  /**
   * Look for a sectionId that's unique in the form of
   * @param section the section you want an id for
   * @param counter a number to add to the name
   * @returns {*|string}
   */
  const createNewSectionId = (section, counter = 1) => {
    const sectionText = section.sectionDisplayName.split(" ").join("")
    const sectionIds = sections.map(section => section.sectionId)
    return sectionIds.includes(`${sectionText}${counter}`) ? createNewSectionId(section, ++counter) : `${sectionText}${counter}`
  }

  const copySection = (section) => {
    let copy = _.cloneDeep(section)
    copy.custom = true
    copy.sectionId = createNewSectionId(section)
    localPagesTemplate.sections.push(copy)
    setSections([...localPagesTemplate.sections])
    setFormIsDirty(true)
  }

  const addCustomSection = () => {
    const newSection = {
      sectionId: undefined, custom: true,
      sectionDisplayName: "", required: false, active: true, isBooleanCollection: false, notes: "",
      fields: []
    }
    localPagesTemplate.sections.push(newSection)
    setSections([...localPagesTemplate.sections])
    setFormIsDirty(true)
  }

  const deleteSection = (delIndex) => {
    sections.splice(delIndex, 1)
    localPagesTemplate.sections = [...sections]
    setSections([...sections])
    setFormIsDirty(true)
  }

  const sectionUpdated = (sectionObj, index) => {
    sections.splice(index, 1, sectionObj)
    localPagesTemplate.sections = sections
    setFormIsDirty(true)
  }

  const defaultSections = sections.filter(section => section.custom == false);

  const onDragEnd = (isCustom, sectionId, result) => {
    if (!result.destination || sectionId !==  result.source.droppableId) {
      return;
    }
    const sorted = localPagesTemplate.sections.sort(x => !x.custom);
    const offset = isCustom ? defaultSections.length : 0;

    const reorderedSections = reorder(
      sorted,
      result.source.index + offset,
      result.destination.index + offset
    );

    setSections([...reorderedSections]);
    setFormIsDirty(true);
    // not sure why the object was clone but now we have to
    localPagesTemplate.sections = [...reorderedSections]
  }



  return (
    <div className="card">
      <div className="card-header d-flex align-items-center card-header-padding-bottom border-bottom">
        <h3 className="flex-grow-1">Template Editor</h3>
        <span className="btn btn-outline-secondary "
              onClick={cancel}> Cancel Changes </span>
        <button className={"btn btn-secondary control-spacer-left" + (!formIsDirty ? " disabled" : "")}
                disabled={saveDisabled()}
                onClick={save}><i className="fa fa-edit"></i>Save and Submit
        </button>
      </div>
      <div className="card-body">
        <div style={{marginBottom: '30px'}} className="">
          <div className="card col-12">
            <div key="row1" className="row p-2">
              <div className="form-group col-md-4">
                <div className="d-flex flex-column">
                  <label className="h6" htmlFor="name">Template Name</label>
                  <input className="form-control text-input" id="name" type="text"
                         value={templateName} onChange={nameChanged} name="name"/>
                </div>
              </div>
              <div className="form-group col-md-4">
                <div className="d-flex flex-column">
                  <label className="h6" htmlFor="language">Language</label>
                  <LanguageSelector
                    onSelectFunction={languageSelected}
                    selectedValue={localPagesTemplate.language}
                  />
                </div>
              </div>
              <div className="form-group col-md-4">
                <div className="d-flex flex-column">
                  <label className="h6" htmlFor="themeId">Theme Id*</label>
                  <input className="form-control text-input" id="themeId" type="text"
                         value={themeId} onChange={themeChanged} name="themeId"/>
                </div>
              </div>
            </div>
            <div className="row p-2">
              <div className="form-group col-md-4">
                <div className="d-flex flex-column">
                  <label className="h6" key="brands-label" htmlFor="brands">Brands*</label>
                  {brands.map((brand, index) => {
                    return <input className="form-control text-input mt-1"
                                  key={"brandname-key" + index} id={index} type="text"
                                  value={brand.name} onChange={e => brandNameChanged(e, index)}
                                  name="brands"/>

                  })}

                </div>
                <div className="d-inline-flex flex-column">
                  <button type="button" className="btn btn-secondary mt-2" onClick={e => addBrand()}>
                    <i className="fa fa-plus"/>
                  </button>
                </div>
              </div>
              <div className="form-group col-md-4">
                <div className="d-flex flex-column">
                  <label key="devhubId-label" className="h6" htmlFor="devhubId">Brand Id*</label>
                  {brands.map((brand, index) => {
                    return <input className="form-control text-input mt-1"
                                  key={"brand.vendorId-key" + index} id={index} type="text"
                                  value={brand.vendorId}
                                  onChange={e => brandIdChanged(e.target.value, index)}
                                  name="brand.vendorId"/>
                  })}
                </div>
              </div>

              <div className="form-group col-md-1">
                <div className="d-flex flex-column">
                  <label key="devhubId-label" htmlFor="minusSign">&nbsp;</label>
                  {brands.map((brand, index) => {
                    return <button type="button" key={"button1-" + index} className="btn btn-primary pb-1 mb-1"
                                   onClick={event => removeBrand(index)}>
                      <i className="fa fa-minus"/>
                    </button>

                  })}
                </div>
              </div>
            </div>
            <SectionHeader key="sectionHeader-key-1" customSection={false}/>
            <DragDropContext onDragEnd={onDragEnd.bind(this, false, "droppable-default")}>
              <Droppable droppableId="droppable-default">
                {(provided, snapshot) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {defaultSections.map((section, index) => (
                      <Draggable key={section.sectionId} draggableId={section.sectionId} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={getItemStyle(
                              snapshot.isDragging,
                              provided.draggableProps.style
                            )}
                          >
                            <TemplateSection
                              key={"section-key-" + section.sectionId}
                              sectionObj={section}
                              copyFunction={copySection}
                              sectionIndex={sections.findIndex(elem => elem == section)}
                              deleteFunction={deleteSection}
                              sectionUpdated={sectionUpdated}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            <SectionHeader key="sectionHeader-key-2" customSection={true}
                           addCustomSectionFunction={addCustomSection}/>
            <DragDropContext onDragEnd={onDragEnd.bind(this, true, "droppable-custom")}>
              <Droppable droppableId="droppable-custom">
                {(provided, snapshot) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {sections.filter(section => section.custom == true).map((section, index) => (
                      <Draggable key={section.sectionId} draggableId={section.sectionId} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={getItemStyle(
                              snapshot.isDragging,
                              provided.draggableProps.style
                            )}
                          >
                            <TemplateSection
                              key={"section-key-" + section.sectionId}
                              sectionObj={section}
                              copyFunction={copySection}
                              sectionIndex={sections.findIndex(elem => elem == section)}
                              deleteFunction={deleteSection}
                              sectionUpdated={sectionUpdated}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        </div>
      </div>
    </div>
  )

}
